import { useState, useCallback, memo } from 'react';
import ToggleButton, { IToggleButtonProps } from '../ToggleButton';
import { IUseFilterButtonReturn } from '../../index';
import Grid from '../Grid';
import Typography from '../Typography';
import { FilterIcon } from '../../icons';
import { mergeSX } from '../../services/helpers';
import {
  B1, AB1, AB3, S1,
} from '../../theme';

export interface IFilterButtonProps extends Omit<
  IToggleButtonProps, 'icon'
>, IUseFilterButtonReturn {}

interface IBuildCircleSXParams {
  isHovered: boolean;
}

const FILTER_ICON = <FilterIcon width={28} height={28} />;

const COLORS = {
  defaultIconColor: AB3,
  defaultButtonBackground: AB1,
  hoveredIconColor: B1,
  hoveredButtonBackground: AB1,
  selectedIconColor: S1,
  selectedButtonBackground: AB3,
  selectedAndHoveredIconColor: S1,
  selectedAndHoveredButtonBackground: B1,
};

const buildSX = () => ({
  position: 'relative',
  display: 'inline-grid',
});

const buildCircleSX = ({ isHovered }: IBuildCircleSXParams) => ({
  position: 'absolute',
  bottom: 0,
  right: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 16,
  height: 16,
  background: S1,
  border: `1px solid ${isHovered ? B1 : AB3}`,
  borderRadius: '50%',
  userSelect: 'none',
});

const FilterButton = (props: IFilterButtonProps) => {
  const {
    isFilterActive,
    numberOfActiveFilters,
    selected,
    sx,
    ...restProps
  } = props;

  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  return (
    <Grid sx={mergeSX(buildSX(), sx)}>
      <ToggleButton
        selected={selected}
        icon={FILTER_ICON}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...(isFilterActive ? COLORS : {})}
        {...restProps}
      />
      {isFilterActive && (
        <Grid sx={buildCircleSX({ isHovered })}>
          <Typography variant="caption" color={isHovered ? B1 : AB3}>
            {numberOfActiveFilters}
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};

export default memo(FilterButton);
